'use client';

import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

// scroll: Next App Router의 네비게이션 후 스크롤-투-탑 여부(기본 true).
//   같은 페이지의 쿼리만 갱신할 때 스크롤 컨테이너가 맨 위로 리셋되는 것을 막으려면 false를 넘긴다.
//   shallow가 true면 무시된다(네비게이션이 일어나지 않으므로).
// shallow: router 대신 native history API로 URL만 갱신한다(기본 false).
//   router.push/replace는 쿼리만 바뀌어도 RSC 페이로드를 다시 받아오고, 그 응답이 도착해야
//   useSearchParams가 갱신된다. 네트워크가 느리면 클릭이 먹지 않는 것처럼 보인다.
//   shallow는 네트워크 왕복 없이 즉시 반영되지만 서버 렌더를 다시 트리거하지 않으므로,
//   서버에서 searchParams를 읽는 페이지(예: menu/list)의 쿼리에는 쓰면 안 된다.
interface UrlQueryNavigateOptions {
  scroll?: boolean;
  shallow?: boolean;
}

export default function useUrlQuery() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getUrlQuery = useCallback(
    ({ key }: { key: string }) => {
      return searchParams.get(key);
    },
    [searchParams],
  );

  const buildUrl = useCallback(
    ({ params }: { params: URLSearchParams }) => {
      const queryString = params.toString();
      return queryString ? `${pathname}?${queryString}` : pathname;
    },
    [pathname],
  );

  const pushUrl = useCallback(
    ({ url, scroll = true, shallow = false }: { url: string } & UrlQueryNavigateOptions) => {
      if (shallow) {
        window.history.pushState(null, '', url);
        return;
      }
      router.push(url, { scroll });
    },
    [router],
  );

  const replaceUrl = useCallback(
    ({ url, scroll = true, shallow = false }: { url: string } & UrlQueryNavigateOptions) => {
      if (shallow) {
        window.history.replaceState(null, '', url);
        return;
      }
      router.replace(url, { scroll });
    },
    [router],
  );

  const setUrlQuery = useCallback(
    ({ key, value, scroll, shallow }: { key: string; value: string } & UrlQueryNavigateOptions) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(key, value);
      pushUrl({ url: buildUrl({ params }), scroll, shallow });
    },
    [searchParams, buildUrl, pushUrl],
  );

  const replaceUrlQuery = useCallback(
    ({ key, value, scroll, shallow }: { key: string; value: string } & UrlQueryNavigateOptions) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(key, value);
      replaceUrl({ url: buildUrl({ params }), scroll, shallow });
    },
    [searchParams, buildUrl, replaceUrl],
  );

  const replaceUrlQueries = useCallback(
    ({
      entries,
      scroll,
      shallow,
    }: { entries: Record<string, string | null> } & UrlQueryNavigateOptions) => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(entries).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key);
          return;
        }
        params.set(key, value);
      });
      replaceUrl({ url: buildUrl({ params }), scroll, shallow });
    },
    [searchParams, buildUrl, replaceUrl],
  );

  const removeUrlQuery = useCallback(
    ({ key, scroll, shallow }: { key: string } & UrlQueryNavigateOptions) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(key);
      replaceUrl({ url: buildUrl({ params }), scroll, shallow });
    },
    [searchParams, buildUrl, replaceUrl],
  );

  const removeUrlQueries = useCallback(
    ({ keys, scroll, shallow }: { keys: string[] } & UrlQueryNavigateOptions) => {
      const params = new URLSearchParams(searchParams.toString());
      keys.forEach((key) => params.delete(key));
      replaceUrl({ url: buildUrl({ params }), scroll, shallow });
    },
    [searchParams, buildUrl, replaceUrl],
  );

  const pushWithUrlQuery = useCallback(
    ({ path, query }: { path: string; query: Record<string, string> }) => {
      const params = new URLSearchParams(query);
      const queryString = params.toString();
      router.push(queryString ? `${path}?${queryString}` : path);
    },
    [router],
  );

  return {
    getUrlQuery,
    setUrlQuery,
    replaceUrlQuery,
    replaceUrlQueries,
    removeUrlQuery,
    removeUrlQueries,
    pushWithUrlQuery,
  };
}
